import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { API } from "../../lib/api";
import { ArrowLeft, Sparkles, TrendingUp, Activity, Lock, ScanLine, Receipt } from "lucide-react";

export default function CustomerPortfolioDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lot, setLot] = useState(null);
  const [insights, setInsights] = useState("");
  const [prediction, setPrediction] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [predLoading, setPredLoading] = useState(false);
  const [animal, setAnimal] = useState(null); // selected animal modal
  const [healthRecords, setHealthRecords] = useState([]);

  const reload = async () => {
    const r = await api.get(`/lots/${id}`);
    setLot(r.data);
  };

  useEffect(() => { reload(); }, [id]);

  const getInsights = async () => {
    setAiLoading(true);
    try { const r = await api.get(`/ai/health-insights/${id}`); setInsights(r.data.insights); }
    catch (_) { toast.error("Failed to fetch insights"); }
    setAiLoading(false);
  };

  const getPrediction = async () => {
    setPredLoading(true);
    try { const r = await api.get(`/ai/profit-prediction/${id}`); setPrediction(r.data); }
    catch (_) { toast.error("Failed to fetch prediction"); }
    setPredLoading(false);
  };

  const requestSell = async () => {
    if (!confirm("Submit a sale request for this lot? Admin will process it.")) return;
    try {
      await api.post("/customer/sell/initiate", { lot_id: lot.id });
      toast.success("Sale request submitted");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to request sale"); }
  };

  const openAnimal = async (a) => {
    setAnimal(a);
    try { const r = await api.get(`/health-records/${a.id}`); setHealthRecords(r.data); }
    catch (_) { setHealthRecords([]); }
  };

  if (!lot) return <div className="text-center text-[#6B5F58] py-12">Loading...</div>;

  const lockUntil = lot.lock_until ? new Date(lot.lock_until) : null;
  const locked = lockUntil && lockUntil > new Date();
  const canSell = lot.status === "in_progress" && !locked;

  return (
    <div>
      <Link to="/portfolio" className="inline-flex items-center gap-1 text-sm text-[#6B5F58] hover:text-[#1F1816] mb-4">
        <ArrowLeft className="w-4 h-4" /> Portfolio
      </Link>

      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#6B5F58]">{lot.lot_code}</div>
          <h1 className="font-display text-3xl md:text-4xl mt-1">{lot.name}</h1>
          <div className="flex items-center gap-3 mt-2 text-sm text-[#6B5F58]">
            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
              lot.status === "in_progress" ? "bg-blue-100 text-blue-800"
                : lot.status === "ready_to_sell" ? "bg-amber-100 text-amber-800"
                : lot.status === "sold_out" ? "bg-[#FCEDED] text-[#D71921]" : "bg-stone-100"}`}>{lot.status.replace("_"," ")}</span>
            {locked && <span className="flex items-center gap-1"><Lock className="w-3.5 h-3.5" /> Locked until {lockUntil.toLocaleDateString()}</span>}
          </div>
        </div>
        <div className="flex gap-2">
          {canSell && (
            <button onClick={requestSell} data-testid="sell-lot-btn" className="px-4 py-2 rounded-xl bg-[#B8862A] text-white text-sm font-medium hover:bg-[#9A6E1A]">
              Request Sale
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Box label="Investment" value={`₹${lot.price_per_lot.toLocaleString()}`} />
        <Box label="Animals" value={lot.animal_count} />
        <Box label="Expenses" value={`₹${(lot.total_expenses || 0).toLocaleString()}`} />
        <Box label={lot.status === "sold_out" ? "Profit" : "Net"} value={`₹${(lot.profit_to_customer || (lot.price_per_lot - (lot.total_expenses||0))).toLocaleString()}`} />
      </div>

      {/* AI cards */}
      <div className="grid lg:grid-cols-2 gap-4 mb-6">
        <div className="gf-card p-5">
          <div className="flex items-center gap-2 text-[#D71921] text-xs font-semibold uppercase tracking-wider">
            <Sparkles className="w-4 h-4" /> AI Health Insight
          </div>
          {insights ? (
            <p className="text-sm text-[#1F1816] mt-3 leading-relaxed whitespace-pre-line">{insights}</p>
          ) : (
            <>
              <p className="text-sm text-[#6B5F58] mt-2">Get an AI-generated summary of your lot's health and recommendations.</p>
              <button onClick={getInsights} disabled={aiLoading} data-testid="ai-insights-btn"
                className="mt-3 px-4 py-2 rounded-xl bg-[#D71921] text-white text-sm disabled:opacity-60">
                {aiLoading ? "Generating..." : "Generate insights"}
              </button>
            </>
          )}
        </div>
        <div className="gf-card p-5">
          <div className="flex items-center gap-2 text-[#D71921] text-xs font-semibold uppercase tracking-wider">
            <TrendingUp className="w-4 h-4" /> AI Profit Predictor
          </div>
          {prediction ? (
            <>
              <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                <div><div className="text-[#6B5F58]">Avg weight</div><div className="font-medium">{prediction.metrics.avg_weight} kg</div></div>
                <div><div className="text-[#6B5F58]">Growth</div><div className="font-medium">{prediction.metrics.growth_pct}%</div></div>
                <div><div className="text-[#6B5F58]">Expenses</div><div className="font-medium">₹{prediction.metrics.expenses}</div></div>
              </div>
              <p className="text-sm text-[#1F1816] mt-3 leading-relaxed whitespace-pre-line">{prediction.prediction}</p>
            </>
          ) : (
            <>
              <p className="text-sm text-[#6B5F58] mt-2">Predict optimal sale time and expected profit.</p>
              <button onClick={getPrediction} disabled={predLoading} data-testid="ai-predict-btn"
                className="mt-3 px-4 py-2 rounded-xl bg-[#D71921] text-white text-sm disabled:opacity-60">
                {predLoading ? "Predicting..." : "Predict profit"}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Animals grid */}
      <h2 className="font-display text-2xl mt-8 mb-3">Animals</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {lot.animals?.map((a) => (
          <button key={a.id} onClick={() => openAnimal(a)} data-testid={`animal-card-${a.tag_number}`}
            className="gf-card p-4 text-left hover:border-[#D71921] transition-all">
            <div className="flex items-center gap-3">
              {a.qr_code_b64 && (
                <img src={a.qr_code_b64} alt={a.tag_number} className="w-14 h-14 rounded-lg border border-[#E8E0D2]" />
              )}
              <div className="flex-1">
                <div className="font-mono text-sm font-medium">{a.tag_number}</div>
                <div className="text-xs text-[#6B5F58]">{a.species} · {a.breed}</div>
                <div className="text-xs text-[#6B5F58]">{a.current_weight_kg}kg · {a.health_status}</div>
              </div>
              <ScanLine className="w-4 h-4 text-[#6B5F58]" />
            </div>
          </button>
        ))}
      </div>

      {/* Expenses */}
      {lot.expenses?.length > 0 && (
        <div className="mt-8">
          <h2 className="font-display text-2xl mb-3">Expenses</h2>
          <div className="gf-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#F8F4E9] text-xs uppercase tracking-wider text-[#6B5F58]">
                <tr>
                  <th className="text-left px-4 py-3">Date</th>
                  <th className="text-left px-4 py-3">Category</th>
                  <th className="text-left px-4 py-3">Description</th>
                  <th className="text-right px-4 py-3">Amount</th>
                </tr>
              </thead>
              <tbody>
                {lot.expenses.map((e) => (
                  <tr key={e.id} className="border-t border-[#E8E0D2]">
                    <td className="px-4 py-3 text-xs text-[#6B5F58]">{(e.incurred_at || "").slice(0,10)}</td>
                    <td className="px-4 py-3"><span className="px-2 py-0.5 rounded-full text-xs bg-[#FCEDED] text-[#D71921]">{e.category}</span></td>
                    <td className="px-4 py-3 text-sm">{e.description || "—"}</td>
                    <td className="px-4 py-3 text-right font-medium">₹{e.amount.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Animal modal */}
      {animal && (
        <div className="fixed inset-0 z-50 backdrop-blur-md bg-stone-900/40 grid place-items-center p-4" onClick={() => setAnimal(null)}>
          <div className="bg-white rounded-2xl border border-[#E8E0D2] max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-[#6B5F58]">{animal.species} · {animal.breed}</div>
                <div className="font-display text-2xl">{animal.tag_number}</div>
              </div>
              {animal.qr_code_b64 && <img src={animal.qr_code_b64} alt="" className="w-20 h-20 rounded-lg border border-[#E8E0D2]" />}
            </div>
            <div className="grid grid-cols-3 gap-2 mt-4 text-center">
              <div className="p-2 rounded-lg bg-[#F8F4E9]"><div className="text-xs text-[#6B5F58]">Initial</div><div className="font-medium">{animal.initial_weight_kg}kg</div></div>
              <div className="p-2 rounded-lg bg-[#F8F4E9]"><div className="text-xs text-[#6B5F58]">Current</div><div className="font-medium">{animal.current_weight_kg}kg</div></div>
              <div className="p-2 rounded-lg bg-[#F8F4E9]"><div className="text-xs text-[#6B5F58]">Age</div><div className="font-medium">{animal.age_months}mo</div></div>
            </div>
            <div className="mt-5">
              <div className="text-xs uppercase tracking-widest text-[#6B5F58] mb-2 flex items-center gap-1"><Activity className="w-3 h-3" /> Health timeline</div>
              {healthRecords.length === 0 && <div className="text-sm text-[#6B5F58]">No records yet.</div>}
              <ul className="space-y-2 max-h-48 overflow-auto pr-1">
                {healthRecords.map((h) => (
                  <li key={h.id} className="text-sm border-l-2 border-[#3F6B3F] pl-3">
                    <div className="text-xs text-[#6B5F58]">{(h.recorded_at || "").slice(0,10)}</div>
                    <div>{h.weight_kg}kg · {h.health_status}</div>
                    {h.notes && <div className="text-xs text-[#6B5F58]">{h.notes}</div>}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Box({ label, value }) {
  return <div className="gf-card p-4"><div className="text-xs uppercase tracking-wider text-[#6B5F58]">{label}</div><div className="font-display text-xl mt-1">{value}</div></div>;
}
